/**
 * mockData.ts — backward-compatible re-exports.
 * All type definitions live in types.ts.
 * All seed data lives in seedData.ts.
 * Runtime-mutable data lives in DataContext.tsx.
 *
 * NOTE: The static exports below (SUPPLIERS, PURCHASE_ORDERS, etc.) are used
 * only as fallbacks. Prefer useData() from DataContext in all page components.
 */

export type { GstTreatment, BillStatus, PoStatus, Supplier, Product, PurchaseOrder, POLineDetail, Bill, BillLineItem } from './types';

export {
  DEFAULT_SUPPLIERS      as SUPPLIERS,
  DEFAULT_PRODUCTS       as PRODUCTS,
  DEFAULT_PURCHASE_ORDERS as PURCHASE_ORDERS,
  DEFAULT_PO_LINE_DETAILS as PO_LINE_DETAILS,
  DEFAULT_BILLS          as BILLS,
  DEFAULT_BILL_LINE_ITEMS as BILL_LINE_ITEMS,
} from './seedData';

// Price books keyed by supplierId → { productCode: unitPrice }
// Primary supplier prices come from real invoice data. Cross-supplier prices added for comparison.
export const PRICE_BOOKS: Record<string, Record<string, number>> = {
  // ── sup11 · Quickensted Welding ───────────────────────────────────────────
  sup11: {
    'QSW-SVC': 585.00,
  },
  // ── sup12 · Hoseforce ─────────────────────────────────────────────────────
  sup12: {
    'HC-WDB-06/16':  1.29,
    '06FH':          9.17,
    'DFBANJO-0614M': 11.71,
    'DFBANJO-0612M': 24.92,
    'BR-HTHT-0608':  6.87,
    '05FH':          9.85,   // also stocks SD's product — slightly higher
    'HC-WDN-10/16':  1.68,   // alternative to SD Hydraulic
  },
  // ── sup13 · SD Hydraulic ──────────────────────────────────────────────────
  sup13: {
    '05FH':          9.20,
    'HC-WDN-10/16':  1.57,
    '06FH':         11.50,   // also stocks Hoseforce product — higher price
    'HC-WDB-06/16':  1.45,   // slightly more expensive than Hoseforce
  },
  // ── sup14 · DTS WA ────────────────────────────────────────────────────────
  sup14: {
    'SGA12SGSD':  2.20,
    'PACMC2240':  1.41,
    'PACMC1300':  0.94,
    'FM40':      24.50,   // also stocks Dasco's first-aid kit — slightly more
  },
  // ── sup15 · Lyons Airconditioning ─────────────────────────────────────────
  sup15: {
    'LY-FK00147': 3750.00,
  },
  // ── sup16 · Dasco Supply Group ────────────────────────────────────────────
  sup16: {
    'MB205':          8.05,
    'FM40':          23.00,
    'FREIGHT-METRO': 11.00,
    'SGA12SGSD':      2.45,  // also stocks — slightly higher than DTS
    'PACMC2240':      1.55,  // more expensive than DTS
  },
  // ── sup17 · Super Motor Spares ────────────────────────────────────────────
  sup17: {
    '90179-22029':  6.01,
    '41214-60020': 11.98,
    '90366-33006': 43.90,
    '41231-60040': 26.02,
    '90366-40111': 68.50,
    '90311-45036': 26.50,
  },
  // ── sup18 · Midland Steel Fabrication ────────────────────────────────────
  sup18: {
    'MSF-ALLY-TRI-3': 338.00,
  },
  // ── sup19 · DCT Parts WA ─────────────────────────────────────────────────
  sup19: {
    '8T4131':      3.40,
    'S-140-080-16':9.05,
    '2135007':     7.79,
    '5416882':   375.55,
  },
  // ── sup20 · Inter-Steel ───────────────────────────────────────────────────
  sup20: {
    'IMAGE-135115': 379.00,
    'IMAGE-132123': 335.00,
    'FREIGHT-IST':   75.00,
  },
  // ── sup21 · Quantum Firetech ──────────────────────────────────────────────
  sup21: {
    'QFT-30L-ENVO': 9900.00,
  },
};

export const PO_LINES = [
  { id: 1, inventoryCode: 'WA5364',   description: 'Air filter',   qty: 1, supplierId: 'sup2', jobType: 'General Service', category: '' },
  { id: 2, inventoryCode: 'WACF0215', description: 'Cabin Filter', qty: 1, supplierId: 'sup2', jobType: 'General Service', category: '' },
  { id: 3, inventoryCode: 'WZ418NM',  description: 'Oil Filter',   qty: 1, supplierId: 'sup2', jobType: 'General Service', category: '' },
  { id: 4, inventoryCode: 'WCF82',    description: 'Fuel Filter',  qty: 1, supplierId: 'sup2', jobType: 'General Service', category: '' },
];

export const BILL_DIFF_DATA = [
  { billId: 'BILL-2026-001', billDate: '2026-02-26', supplier: 'JAS Oceania Pty Ltd',          billNo: 'INV-5879691',  billAmount: 181.83, paidAmount: 181.83, status: 'Paid',     poNo: 'PO-4560', poDate: '2026-02-25', poAmount: 181.83, received: 181.83, variance: 0,     varPct: 0,   match: 'Matched'   },
  { billId: 'BILL-2026-002', billDate: '2026-03-18', supplier: 'Auto Parts Distribution (NEW)', billNo: 'APD-38KINGYS', billAmount: 64.97,  paidAmount: null,   status: 'Approved', poNo: 'PO-4571', poDate: '2026-03-18', poAmount: 64.97,  received: 59.06, variance: 0,     varPct: 0,   match: 'Matched'   },
  { billId: 'BILL-2026-003', billDate: '2026-03-10', supplier: 'Repco',                        billNo: 'RP-109234',    billAmount: 248.01, paidAmount: null,   status: 'Approved', poNo: 'PO-4555', poDate: '2026-03-08', poAmount: 248.01, received: 225.46,variance: 22.55, varPct: 10,  match: 'Over'      },
  { billId: 'BILL-2026-004', billDate: '2026-03-21', supplier: '24/7 Access Pty Ltd',          billNo: '247-88213',   billAmount: 86.50,  paidAmount: null,   status: 'Draft',    poNo: 'PO-4575', poDate: '2026-03-21', poAmount: 92.69,  received: 0,     variance: 86.50, varPct: 100, match: 'Unmatched' },
];
